import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, Users, Clock, Phone, ChevronRight, ChevronLeft, Play, X,
  Car, Gauge, Music2, Cog, Luggage, Crown, Shield, Tag, FileCheck,
} from 'lucide-react';
import { allFleetVehicles } from '../data/fleetData';
import { getVehicleImage } from '../utils/vehicleImages';
import { matchToRateSheet } from '../utils/rateSheetMatch';

const API = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('http') ? url : `${API}${url}`;

const SPEC_ICONS = {
  engine: Gauge,
  interior: Car,
  sound: Music2,
  drivetrain: Cog,
  luggage: Luggage,
};

const VehicleDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const staticVehicle = allFleetVehicles.find(v => v.slug === slug);
  const [media, setMedia] = useState({ images: [], videos: [] });
  const [dynamicVehicle, setDynamicVehicle] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(null);
  const [loading, setLoading] = useState(!staticVehicle);

  useEffect(() => {
    if (!slug) return;
    Promise.all([
      fetch(`${API}/api/fleet-media/all`).then(r => r.ok ? r.json() : { vehicles: [] }),
      fetch(`${API}/api/pricing/vehicles`).then(r => r.ok ? r.json() : { vehicles: [] }),
    ]).then(([mediaData, pricingData]) => {
      const mediaMatch = (mediaData.vehicles || []).find(v => v.vehicle_slug === slug);
      if (mediaMatch) {
        setMedia({ images: mediaMatch.images || [], videos: mediaMatch.videos || [] });
      }

      if (!staticVehicle) {
        const pricingList = Array.isArray(pricingData) ? pricingData : (pricingData.vehicles || []);
        const pricingMatch = pricingList.find(v => v.vehicle_id === slug);

        if (pricingMatch) {
          const pName = (pricingMatch.vehicle_name || '').toLowerCase();
          const pWords = pName.replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(w => w.length > 4);
          const fuzzyMedia = !mediaMatch && pWords.length > 0 && (mediaData.vehicles || []).find(m => {
            const mName = (m.vehicle_name || '').toLowerCase();
            return pWords.some(w => mName.includes(w));
          });
          if (fuzzyMedia) {
            setMedia({ images: fuzzyMedia.images || [], videos: fuzzyMedia.videos || [] });
          }

          const isRental = pricingMatch.category === 'rental' || pricingMatch.category === 'rental_ke';
          setDynamicVehicle({
            slug: pricingMatch.vehicle_id,
            tag: pricingMatch.tag || pricingMatch.category,
            name: pricingMatch.vehicle_name,
            pax: pricingMatch.pax || '4',
            minHrs: pricingMatch.min_hours || 0,
            amenities: [],
            ideal: '',
            rate: pricingMatch.retail_hourly || 0,
            memberRate: pricingMatch.member_hourly || pricingMatch.retail_hourly || 0,
            dailyRate: pricingMatch.daily_rate || 0,
            isRental,
            category: pricingMatch.category || 'concierge',
            description: pricingMatch.vehicle_name,
            specs: {},
          });
        } else if (mediaMatch) {
          const rateMatch = matchToRateSheet(mediaMatch.vehicle_name, pricingList.filter(p => p.category !== 'rental'));
          setDynamicVehicle({
            slug: mediaMatch.vehicle_slug,
            tag: mediaMatch.category === 'exotic' ? 'Exotic' : mediaMatch.category === 'group' ? 'Group' : 'Concierge',
            name: mediaMatch.vehicle_name,
            pax: rateMatch?.pax || (mediaMatch.category === 'group' ? 14 : 4),
            minHrs: rateMatch?.min_hours || (mediaMatch.category === 'exotic' ? 4 : 0),
            amenities: [],
            ideal: '',
            rate: rateMatch?.retail_hourly || 0,
            memberRate: rateMatch?.member_hourly || rateMatch?.retail_hourly || 0,
            category: mediaMatch.category || 'concierge',
            description: mediaMatch.vehicle_name,
            specs: {},
          });
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const vehicle = staticVehicle || dynamicVehicle;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="vehicle-not-found">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Vehicle Not Found</h1>
          <Button onClick={() => navigate('/fleet-gallery')} variant="outline" className="border-gray-700 text-gray-300">Back to Fleet Gallery</Button>
        </div>
      </div>
    );
  }

  const similar = allFleetVehicles
    .filter(v => v.category === vehicle.category && v.slug !== vehicle.slug)
    .slice(0, 3);

  const gallery = media.images.length > 0 ? media.images.map(i => i.url) : [getVehicleImage(slug)].filter(Boolean);
  const hasVideos = media.videos.length > 0;
  const specEntries = Object.entries(vehicle.specs || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  const memberSavings = (vehicle.rate || 0) - (vehicle.memberRate || 0);

  const nextImg = () => setActiveImage(i => (i + 1) % gallery.length);
  const prevImg = () => setActiveImage(i => (i - 1 + gallery.length) % gallery.length);

  const bookPath = vehicle.isRental ? `/booking/rental?vehicle=${vehicle.slug}` : `/booking?vehicle=${vehicle.slug}`;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="vehicle-detail-page">
      {/* Hero Gallery */}
      <div className="relative">
        <div className="h-[360px] sm:h-[480px] overflow-hidden bg-gray-900">
          {gallery[activeImage] && (
            <img
              src={resolveUrl(gallery[activeImage])}
              alt={vehicle.name}
              className="w-full h-full object-cover"
              data-testid="vehicle-detail-hero-img"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
          {gallery.length > 1 && (
            <>
              <button onClick={prevImg} className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full" data-testid="gallery-prev"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={nextImg} className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full" data-testid="gallery-next"><ChevronRight className="h-5 w-5" /></button>
              <div className="absolute top-4 right-4 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full">{activeImage + 1} / {gallery.length}</div>
            </>
          )}
        </div>
        <div className="absolute top-6 left-6">
          <button onClick={() => navigate('/fleet-gallery')} className="flex items-center gap-2 text-gray-300 hover:text-white text-sm bg-black/50 backdrop-blur-sm px-4 py-2 rounded-lg" data-testid="back-to-fleet-btn">
            <ArrowLeft className="h-4 w-4" /> Back to Fleet
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-black bg-red-600 text-white px-3 py-1.5 rounded-md tracking-widest uppercase shadow-lg" data-testid="chauffeured-badge">
                <Crown className="h-3 w-3" /> {vehicle.isRental ? 'Self-Drive Rental' : 'Chauffeured Service'}
              </span>
              <span className="text-[10px] sm:text-xs font-bold tracking-widest text-gray-300 uppercase">{(vehicle.tag || vehicle.category || '').toString()}</span>
              {vehicle.category === 'exotic' && (
                <span className="text-[9px] font-bold bg-white/10 text-gray-200 px-2.5 py-1 rounded-full tracking-wider uppercase">48–72hr notice required</span>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-2" data-testid="vehicle-detail-title">{vehicle.name}</h1>
            <div className="flex items-center gap-5 text-sm text-gray-400">
              <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {vehicle.pax} passengers</span>
              {vehicle.minHrs > 0 && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {vehicle.minHrs} hr minimum</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail strip */}
      {gallery.length > 1 && (
        <div className="max-w-6xl mx-auto px-6 pt-6">
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="gallery-thumbs">
            {gallery.slice(0, 12).map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                className={`h-20 rounded-lg overflow-hidden border-2 transition-all ${activeImage === i ? 'border-red-600' : 'border-transparent hover:border-gray-700'}`}
                data-testid={`gallery-thumb-${i}`}
              >
                <img src={resolveUrl(img)} alt={`${vehicle.name} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            {vehicle.description && vehicle.description !== vehicle.name && (
              <div data-testid="vehicle-description">
                <h2 className="text-lg font-bold mb-3">About This Vehicle</h2>
                <p className="text-sm text-gray-400 leading-relaxed">{vehicle.description}</p>
              </div>
            )}

            {/* Specs */}
            {specEntries.length > 0 && (
              <div data-testid="vehicle-specs">
                <h2 className="text-lg font-bold mb-4">Specifications</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {specEntries.map(([key, value]) => {
                    const Icon = SPEC_ICONS[key] || Cog;
                    return (
                      <div key={key} className="bg-gray-950 border border-gray-800 rounded-xl p-4" data-testid={`spec-${key}`}>
                        <Icon className="h-4 w-4 text-red-500 mb-2" />
                        <p className="text-[10px] text-gray-600 font-bold uppercase tracking-wider">{key}</p>
                        <p className="text-sm font-bold text-white">{value}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Amenities */}
            {vehicle.amenities && vehicle.amenities.length > 0 && (
              <div data-testid="vehicle-amenities">
                <h2 className="text-lg font-bold mb-4">Amenities & Features</h2>
                <div className="grid sm:grid-cols-2 gap-2">
                  {vehicle.amenities.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-950 border border-gray-800 rounded-lg px-4 py-2.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      <span className="text-sm text-gray-300">{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ideal For */}
            {vehicle.ideal && (
              <div data-testid="vehicle-ideal">
                <h2 className="text-lg font-bold mb-4">Ideal For</h2>
                <div className="flex flex-wrap gap-2">
                  {vehicle.ideal.split(', ').map((use, i) => (
                    <span key={i} className="text-xs font-semibold bg-gray-950 border border-gray-800 text-gray-300 px-4 py-2 rounded-full">{use}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Videos */}
            {hasVideos && (
              <div data-testid="vehicle-videos">
                <h2 className="text-lg font-bold mb-4">Videos</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {media.videos.map((vid, i) => (
                    <div
                      key={i}
                      className="relative rounded-xl overflow-hidden bg-gray-950 border border-gray-800 cursor-pointer group"
                      onClick={() => setShowVideoModal(vid)}
                    >
                      <video src={resolveUrl(vid.url)} className="w-full aspect-video object-cover" preload="metadata" />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                        <div className="h-14 w-14 bg-red-600 rounded-full flex items-center justify-center">
                          <Play className="h-6 w-6 text-white ml-1" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How Booking Works */}
            <div data-testid="how-it-works">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><FileCheck className="h-5 w-5 text-red-500" /> How Booking Works</h2>
              <div className="grid sm:grid-cols-4 gap-3">
                {(vehicle.isRental ? [
                  { n: 1, t: 'Submit Request', d: 'Tell us your dates & info' },
                  { n: 2, t: 'ACE Reviews', d: 'Within 1 business hour' },
                  { n: 3, t: 'Approval SMS', d: 'Confirmed via text' },
                  { n: 4, t: 'Pick Up', d: 'Sign agreement & drive' },
                ] : [
                  { n: 1, t: 'Book Online', d: 'Pick your date, time & pickup' },
                  { n: 2, t: 'ACE Confirms', d: 'Availability confirmed fast' },
                  { n: 3, t: 'Chauffeur Assigned', d: 'Trip details sent via text' },
                  { n: 4, t: 'We Arrive', d: 'Sit back — we drive' },
                ]).map(s => (
                  <div key={s.n} className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                    <div className="h-7 w-7 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-bold mb-2">{s.n}</div>
                    <p className="text-sm font-bold">{s.t}</p>
                    <p className="text-[11px] text-gray-500">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Pricing Card */}
            <Card className="bg-gray-950 border-2 border-red-600 p-6 sticky top-6" data-testid="vehicle-pricing-card">
              <h3 className="text-base font-bold text-gray-400 mb-4">{vehicle.isRental ? 'Daily Rate' : 'Hourly Rate'}</h3>
              {vehicle.isRental ? (
                <div className="space-y-3 mb-6">
                  <div className="flex items-baseline gap-2">
                    <p className="text-4xl font-black text-white">${vehicle.dailyRate || 0}</p>
                    <p className="text-sm text-gray-500">/day</p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Mileage</p>
                    <p className="font-bold text-white text-sm">200 mi/day included</p>
                    <p className="text-[10px] text-gray-500 mt-1">Additional miles billed at $1.50/mi</p>
                  </div>
                  <p className="text-xs text-gray-500">25+ age · security deposit · full coverage insurance required.</p>
                </div>
              ) : (
                <div className="space-y-3 mb-6">
                  {vehicle.rate > 0 ? (
                    <>
                      <div className="flex items-baseline gap-2">
                        <p className="text-4xl font-black text-white">${vehicle.rate}</p>
                        <p className="text-sm text-gray-500">/hr retail</p>
                      </div>
                      {memberSavings > 0 && (
                        <div className="flex items-baseline gap-2">
                          <p className="text-xl font-black text-red-500">${vehicle.memberRate}</p>
                          <p className="text-xs text-gray-500">/hr member rate</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-2xl font-black text-white">By Request</p>
                  )}
                  {vehicle.minHrs > 0 && vehicle.rate > 0 && (
                    <div className="bg-gray-900 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Minimum</p>
                      <p className="font-bold text-white text-sm">{vehicle.minHrs} hours (${vehicle.rate * vehicle.minHrs} retail)</p>
                      {vehicle.category === 'concierge' && (
                        <p className="text-[10px] text-gray-500 mt-1">No minimum for point-to-point transfers</p>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">Taxes & gratuity not included. Professional chauffeur included in every booking.</p>
                </div>
              )}
              <Button
                onClick={() => navigate(bookPath)}
                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white text-sm font-bold mb-3"
                data-testid="vehicle-book-btn"
              >
                {vehicle.isRental ? 'Reserve Self-Drive Rental' : 'Book This Vehicle'}
              </Button>
              <a href="tel:6788463009" className="flex items-center justify-center gap-2 w-full h-10 border border-gray-700 text-gray-300 hover:bg-gray-900 text-sm font-semibold rounded-lg" data-testid="call-ace-btn">
                <Phone className="h-4 w-4" /> Call (678) 846-3009
              </a>
            </Card>

            {/* Highlights */}
            <Card className="bg-gray-950 border-gray-800 p-5" data-testid="highlights-card">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Crown className="h-4 w-4 text-red-500" />
                  <div><p className="text-[10px] text-gray-600 uppercase tracking-wider">Service</p><p className="text-sm font-bold">{vehicle.isRental ? 'Self-Drive' : 'Chauffeured'}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <Tag className="h-4 w-4 text-red-500" />
                  <div><p className="text-[10px] text-gray-600 uppercase tracking-wider">Category</p><p className="text-sm font-bold capitalize">{(vehicle.tag || vehicle.category || '').toString()}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="h-4 w-4 text-red-500" />
                  <div><p className="text-[10px] text-gray-600 uppercase tracking-wider">Capacity</p><p className="text-sm font-bold">{vehicle.pax} passengers</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="h-4 w-4 text-red-500" />
                  <div><p className="text-[10px] text-gray-600 uppercase tracking-wider">Fleet</p><p className="text-sm font-bold">ACE Standard · inspected & detailed</p></div>
                </div>
              </div>
            </Card>

            {/* Membership Upsell */}
            {memberSavings > 0 && !vehicle.isRental && (
              <Card className="bg-gray-950 border-gray-800 p-5" data-testid="membership-upsell">
                <h3 className="text-base font-bold mb-2 text-white">Save ${memberSavings}/hr</h3>
                <p className="text-xs text-gray-500 mb-4">ACE members receive preferred pricing on all fleet vehicles{vehicle.minHrs > 0 ? ` — $${memberSavings * vehicle.minHrs} saved on the minimum booking alone` : ''}.</p>
                <Button onClick={() => navigate('/membership')} variant="outline" className="w-full border-gray-700 text-gray-300 hover:bg-gray-900 text-xs h-9">
                  View Membership
                </Button>
              </Card>
            )}
          </div>
        </div>

        {/* Similar Vehicles */}
        {similar.length > 0 && (
          <div className="mt-16 pt-12 border-t border-gray-900">
            <h2 className="text-lg font-bold mb-6">Similar Vehicles</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {similar.map((v, idx) => (
                <Card
                  key={idx}
                  className="bg-gray-950 border-gray-800 overflow-hidden hover:border-gray-600 transition-all cursor-pointer group"
                  onClick={() => { navigate(`/fleet/${v.slug}`); window.scrollTo({ top: 0 }); }}
                  data-testid={`similar-vehicle-${idx}`}
                >
                  <div className="h-40 bg-gray-900 overflow-hidden">
                    <img src={getVehicleImage(v.slug)} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <span className="text-[10px] font-bold text-red-500 tracking-wider uppercase">{v.tag}</span>
                    <h3 className="text-sm font-bold text-white mt-1 mb-2 group-hover:text-red-500 transition-colors">{v.name}</h3>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                      <span>{v.pax} pax</span>
                      <span>{v.minHrs}hr min</span>
                    </div>
                    <div className="flex justify-between items-end">
                      <p className="font-black text-white">${v.rate}<span className="text-[10px] text-gray-500 font-normal">/hr</span></p>
                      <span className="flex items-center gap-1 text-xs text-red-500 font-bold">View <ChevronRight className="h-3 w-3" /></span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Photo Gallery Section at bottom */}
      {media.images.length > 1 && (
        <div className="border-t border-gray-900 bg-gray-950" data-testid="vehicle-photo-section">
          <div className="max-w-6xl mx-auto px-6 py-12">
            <div className="flex items-end justify-between mb-6 pb-3 border-b border-gray-900">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">Gallery</p>
                <h2 className="text-2xl sm:text-3xl font-black mt-1">{vehicle.name} · Photos</h2>
                <p className="text-xs text-gray-500 mt-1">{media.images.length} photos</p>
              </div>
              <Button onClick={() => navigate(bookPath)} className="hidden sm:inline-flex bg-red-600 hover:bg-red-700 text-xs h-9" data-testid="bottom-book-btn">
                {vehicle.isRental ? 'Reserve This Vehicle' : 'Book This Vehicle'}
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bottom-gallery-grid">
              {media.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => { setActiveImage(i); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-900 border border-gray-800 hover:border-red-500 transition-all"
                  data-testid={`bottom-gallery-photo-${i}`}
                >
                  <img src={resolveUrl(img.url)} alt={`${vehicle.name} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <span className="text-[10px] uppercase tracking-widest text-white font-bold">Photo {i + 1} of {media.images.length}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setShowVideoModal(null)}>
          <div className="relative max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowVideoModal(null)} className="absolute -top-10 right-0 text-white hover:text-red-400">
              <X className="h-8 w-8" />
            </button>
            <video src={resolveUrl(showVideoModal.url)} className="w-full rounded-lg" controls autoPlay />
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetailPage;
